(function () {
  const LINE_SELECTOR = "[data-cart-vehicle-line]";
  const BLOCKING_LINE_SELECTOR = "[data-cart-vehicle-blocking]";
  const APPLY_SELECTOR = "[data-cart-vehicle-apply]";
  const HELPER_SELECTOR = "[data-cart-vehicle-helper]";
  const SELECT_SELECTOR = "[data-cart-vehicle-select]";
  const REMOVE_SELECTOR = "[data-cart-vehicle-remove]";
  const STORAGE_KEY = "vehicle_fitment_history";

  class CartVehiclePreflight {
    constructor() {
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.refresh = this.refresh.bind(this);
      this.refreshSoon = this.refreshSoon.bind(this);
      this.onMutation = this.onMutation.bind(this);

      document.addEventListener("click", this.onClick);
      document.addEventListener("change", this.onChange);
      document.addEventListener("vehicle-fitment:changed", this.refresh);
      window.addEventListener("vehicle-fitment:changed", this.refresh);
      window.addEventListener("vehicle-fitment-index:ready", this.refresh);
      this.observeCartSectionChanges();

      if (window.vehicleFitmentIndexReady?.then) {
        window.vehicleFitmentIndexReady.then(this.refresh).catch(this.refresh);
      }

      if (typeof subscribe === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        subscribe(PUB_SUB_EVENTS.cartItemAdd, this.refreshSoon);
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.refreshSoon);
        subscribe(PUB_SUB_EVENTS.cartItemRemove, this.refreshSoon);
        subscribe(PUB_SUB_EVENTS.cartItemQuantityUpdate, this.refreshSoon);
      }

      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", this.refresh, { once: true });
      } else {
        this.refresh();
      }
    }

    refreshSoon() {
      window.setTimeout(this.refresh, 0);
    }

    observeCartSectionChanges() {
      if (!window.MutationObserver || !document.documentElement) {
        return;
      }

      this.observer = new MutationObserver(this.onMutation);
      this.observer.observe(document.documentElement, {
        childList: true,
        subtree: true,
      });
    }

    onMutation(mutations) {
      const shouldRefresh = mutations.some((mutation) => {
        return Array.from(mutation.addedNodes).some((node) => this.nodeContainsCartVehicleUi(node));
      });

      if (shouldRefresh) {
        this.refreshSoon();
      }
    }

    nodeContainsCartVehicleUi(node) {
      if (!node || node.nodeType !== Node.ELEMENT_NODE) {
        return false;
      }

      if (
        node.matches?.(LINE_SELECTOR) ||
        node.matches?.("[data-cart-vehicle-preflight]") ||
        node.matches?.("cart-items, mini-cart")
      ) {
        return true;
      }

      return Boolean(
        node.querySelector?.(`${LINE_SELECTOR}, [data-cart-vehicle-preflight], cart-items, mini-cart`),
      );
    }

    refresh() {
      const currentVehicle = this.currentVehicle();
      const hasUnresolvedLines = Boolean(document.querySelector(BLOCKING_LINE_SELECTOR));

      this.syncCheckoutState(hasUnresolvedLines);

      document.querySelectorAll(LINE_SELECTOR).forEach((line) => {
        const button = line.querySelector(APPLY_SELECTOR);
        const helper = line.querySelector(HELPER_SELECTOR);
        this.populateVehicleSelect(line, currentVehicle);
        const selectedVehicle = this.selectedVehicleForLine(line);
        const state = this.lineVehicleState(line, selectedVehicle || currentVehicle);

        line.classList.toggle("has-compatible-current-vehicle", state.compatible);

        if (button) {
          button.disabled = !state.canApply;
          button.setAttribute("aria-disabled", state.canApply ? "false" : "true");
        }

        if (!helper) {
          return;
        }

        helper.textContent = this.helperText(state);
      });
    }

    syncCheckoutState(hasUnresolvedLines = Boolean(document.querySelector(BLOCKING_LINE_SELECTOR))) {
      if (!hasUnresolvedLines) {
        document.querySelectorAll("[data-cart-vehicle-preflight]").forEach((banner) => banner.remove());
      }

      document.querySelectorAll('button[name="checkout"]').forEach((button) => {
        button.disabled = hasUnresolvedLines;
        button.toggleAttribute("aria-disabled", hasUnresolvedLines);
      });

      document.querySelectorAll(".cart-dynamic-checkout-buttons").forEach((container) => {
        container.classList.toggle("hidden", hasUnresolvedLines);
      });
    }

    async onClick(event) {
      const target = this.eventElement(event);
      const removeButton = target?.closest(REMOVE_SELECTOR);
      if (removeButton) {
        event.preventDefault();
        await this.removeVehicleFromLine(removeButton);
        return;
      }

      const button = target?.closest(APPLY_SELECTOR);
      if (!button) {
        return;
      }

      event.preventDefault();

      const line = button.closest(LINE_SELECTOR);

      if (!line) {
        this.refresh();
        return;
      }

      const state = this.lineVehicleState(line, this.selectedVehicleForLine(line));
      if (!state.canApply) {
        this.refresh();
        return;
      }

      await this.applyVehicleToLine(line, button, state);
    }

    onChange(event) {
      const target = this.eventElement(event);
      if (!target?.closest(SELECT_SELECTOR)) {
        return;
      }

      this.refresh();
    }

    eventElement(event) {
      return event.target instanceof Element ? event.target : event.target?.parentElement || null;
    }

    currentVehicle() {
      const controllerData = window.vehicleFitmentController?.getHistory?.();
      if (controllerData?.current) {
        return controllerData.current;
      }

      const storageData = window.ThemeStorage?.getJson?.(STORAGE_KEY, {});
      if (storageData?.current) {
        return storageData.current;
      }

      try {
        const localStorageVehicle = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}")?.current;
        if (localStorageVehicle) {
          return localStorageVehicle;
        }
      } catch (error) {
        // Fall through to the visible current-vehicle card.
      }

      return this.currentVehicleFromDom();
    }

    populateVehicleSelect(line, currentVehicle) {
      const select = line.querySelector(SELECT_SELECTOR);
      if (!select) {
        return;
      }

      const vehicles = this.compatibleVehiclesForLine(line);
      const signature = vehicles.map((vehicle) => `${vehicle.id}:${vehicle.displayName}`).join("|");
      const selectedBeforeRefresh = select.value;
      const currentVehicleId = this.vehicleId(currentVehicle);
      const shouldPreselectCurrent =
        !selectedBeforeRefresh &&
        currentVehicleId &&
        vehicles.some((vehicle) => vehicle.id === currentVehicleId);

      if (select.dataset.fitmentOptionsSignature !== signature) {
        const placeholder = document.createElement("option");
        placeholder.value = "";
        placeholder.textContent = vehicles.length
          ? "Choose a vehicle fitment"
          : "No compatible fitments found";

        select.replaceChildren(
          placeholder,
          ...vehicles.map((vehicle) => {
            const option = document.createElement("option");
            option.value = vehicle.id;
            option.textContent = vehicle.displayName;
            option.dataset.displayName = vehicle.displayName;
            return option;
          }),
        );
        select.dataset.fitmentOptionsSignature = signature;
      }

      if (selectedBeforeRefresh && vehicles.some((vehicle) => vehicle.id === selectedBeforeRefresh)) {
        select.value = selectedBeforeRefresh;
      } else if (shouldPreselectCurrent) {
        select.value = currentVehicleId;
      } else if (!vehicles.some((vehicle) => vehicle.id === select.value)) {
        select.value = "";
      }

      select.disabled = !vehicles.length;
    }

    compatibleVehiclesForLine(line) {
      const productIds = this.productFitmentIds(line);

      return Array.from(productIds)
        .map((id) => {
          const displayName = this.vehicleDisplayName({ id }, id);
          return displayName ? { id, displayName } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.displayName.localeCompare(b.displayName));
    }

    selectedVehicleForLine(line) {
      const select = line.querySelector(SELECT_SELECTOR);
      const id = this.normalizeFitmentId(select?.value);
      if (!id) {
        return null;
      }

      const option = select?.selectedOptions?.[0];
      return {
        id,
        displayName:
          option?.dataset.displayName ||
          option?.textContent?.trim() ||
          this.vehicleDisplayName({ id }, id),
      };
    }

    async removeVehicleFromLine(button) {
      const properties = this.lineProperties(button);
      delete properties.Fitment;

      button.disabled = true;
      button.setAttribute("aria-disabled", "true");
      button.classList.add("loading");

      try {
        const response = await fetch(this.cartChangeUrl(), {
          ...fetchConfig("javascript"),
          headers: {
            ...fetchConfig("javascript").headers,
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            line: Number(button.dataset.lineIndex),
            quantity: Number(button.dataset.lineQuantity) || 1,
            properties,
          }),
        });

        const parsed = await response.json();
        if (!response.ok || parsed.errors) {
          throw new Error(parsed.errors || parsed.description || window.cartStrings?.error || "Cart update failed");
        }

        await this.refreshCartSections();
        this.announce("Vehicle fitment was removed from your cart item.");
        this.syncCheckoutState();
      } catch (error) {
        console.error(error);
        this.announce(error.message || window.cartStrings?.error || "Cart update failed");
      } finally {
        button.classList.remove("loading");
        this.refresh();
      }
    }

    currentVehicleFromDom() {
      const currentCard = document.querySelector(".fitment-state-card--current");
      const currentVehicleControl =
        currentCard
          ?.closest(".fitment-state-card-wrap")
          ?.querySelector(".js-change-vehicle[data-vehicle-id]") ||
        document.querySelector(".fitment-state-card__change.js-change-vehicle[data-vehicle-id]");

      const id = currentVehicleControl?.dataset.vehicleId;
      if (!id) {
        return null;
      }

      return {
        id,
        displayName:
          currentVehicleControl.dataset.vehicleLabel ||
          currentCard?.querySelector(".fitment-state-card__title")?.textContent?.trim() ||
          "",
      };
    }

    lineMatchesVehicle(line, vehicle) {
      return this.lineVehicleState(line, vehicle).compatible;
    }

    lineVehicleState(line, vehicle) {
      const id = this.vehicleId(vehicle);

      if (!id) {
        return {
          canApply: false,
          compatible: false,
          displayName: "",
          status: "missing",
        };
      }

      const compatible = this.productFitmentIds(line).has(id);
      if (!compatible) {
        return {
          canApply: false,
          compatible: false,
          displayName: this.vehicleDisplayName(vehicle, id),
          status: "incompatible",
        };
      }

      const displayName = this.vehicleDisplayName(vehicle, id);
      if (!displayName) {
        return {
          canApply: false,
          compatible: true,
          displayName: "",
          id,
          status: "loading",
        };
      }

      return {
        canApply: true,
        compatible: true,
        displayName,
        id,
        status: "ready",
      };
    }

    helperText(state) {
      if (state.status === "ready") {
        return `Selected vehicle: ${state.displayName}. Apply it to this item before checkout.`;
      }

      if (state.status === "loading") {
        return "Your selected vehicle appears to fit this item, but vehicle details are still loading. Try again in a moment.";
      }

      if (state.status === "incompatible") {
        return "The selected vehicle does not fit this item. Choose a compatible vehicle, then apply it here.";
      }

      return "Choose a compatible vehicle using this dropdown selector.";
    }

    vehicleId(vehicle) {
      return this.normalizeFitmentId(vehicle?.id || vehicle?.gid || vehicle?.value);
    }

    vehicleDisplayName(vehicle, normalizedId = this.vehicleId(vehicle)) {
      const directName = [
        vehicle?.displayName,
        vehicle?.name,
        vehicle?.title,
        vehicle?.label,
      ]
        .map((value) => String(value || "").trim())
        .find(Boolean);

      if (directName) {
        return directName;
      }

      const metadata = window.vehicleFitmentController?.metadataForFitment?.(normalizedId);
      if (metadata?.displayName) {
        return String(metadata.displayName).trim();
      }

      const indexed = this.indexedFitment(normalizedId);
      return String(indexed?.name || indexed?.displayName || "").trim();
    }

    indexedFitment(normalizedId) {
      if (!normalizedId || !Array.isArray(window.vehicleFitmentIndex)) {
        return null;
      }

      return window.vehicleFitmentIndex.find((fitment) => {
        return [
          fitment?.id,
          fitment?.gid,
          fitment?.admin_graphql_api_id,
        ].some((id) => this.normalizeFitmentId(id) === normalizedId);
      }) || null;
    }

    productFitmentIds(line) {
      return new Set(
        String(line.dataset.productFitmentIds || "")
          .split("|")
          .map((id) => this.normalizeFitmentId(id))
          .filter(Boolean),
      );
    }

    normalizeFitmentId(value) {
      const id = String(value || "").trim();

      if (!id) {
        return "";
      }

      const gidMatch = id.match(/^gid:\/\/shopify\/Metaobject\/(\d+)$/);
      if (gidMatch) {
        return gidMatch[1];
      }

      const numericMatch = id.match(/^\d+$/);
      if (numericMatch) {
        return numericMatch[0];
      }

      const trailingNumericMatch = id.match(/\/(\d+)(?:\?.*)?$/);
      if (trailingNumericMatch) {
        return trailingNumericMatch[1];
      }

      return id;
    }

    async applyVehicleToLine(line, button, vehicle) {
      const properties = this.lineProperties(line);
      properties.Fitment = vehicle.displayName;
      this.normalizeBundleRepairProperties(properties);

      button.disabled = true;
      button.setAttribute("aria-disabled", "true");
      button.classList.add("loading");

      try {
        const response = await fetch(this.cartChangeUrl(), {
          ...fetchConfig("javascript"),
          headers: {
            ...fetchConfig("javascript").headers,
            "X-Requested-With": "XMLHttpRequest",
          },
          body: JSON.stringify({
            line: Number(line.dataset.lineIndex),
            quantity: Number(line.dataset.lineQuantity) || 1,
            properties,
          }),
        });

        const parsed = await response.json();
        if (!response.ok || parsed.errors) {
          throw new Error(parsed.errors || parsed.description || window.cartStrings?.error || "Cart update failed");
        }

        await this.refreshCartSections();
        this.announce(`${vehicle.displayName} was applied to your cart item.`);
        this.syncCheckoutState();
        this.focusAfterRepair();
      } catch (error) {
        console.error(error);
        this.announce(error.message || window.cartStrings?.error || "Cart update failed");
        const helper = line.querySelector(HELPER_SELECTOR);
        if (helper) {
          helper.textContent = "We could not apply the selected vehicle. Please try again.";
        }
      } finally {
        button.classList.remove("loading");
        this.refresh();
      }
    }

    normalizeBundleRepairProperties(properties) {
      if (properties._bundle_type === "merged") {
        properties._bundle_type = "main";
      }
    }

    lineProperties(line) {
      const properties = {};
      const raw = line.dataset.lineProperties;

      if (!raw) {
        return properties;
      }

      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (error) {
        return properties;
      }

      if (Array.isArray(parsed)) {
        parsed.forEach((entry) => {
          if (Array.isArray(entry)) {
            this.setProperty(properties, entry[0], entry[1]);
            return;
          }

          if (entry && typeof entry === "object") {
            this.setProperty(
              properties,
              entry.first || entry.name || entry.key,
              entry.last ?? entry.value,
            );
          }
        });
        return properties;
      }

      if (parsed && typeof parsed === "object") {
        Object.entries(parsed).forEach(([key, value]) => this.setProperty(properties, key, value));
      }

      return properties;
    }

    setProperty(properties, key, value) {
      if (!key || value === null || value === undefined || value === "") {
        return;
      }

      properties[key] = String(value);
    }

    cartChangeUrl() {
      const url = window.routes?.cart_change_url || "/cart/change";
      return url.endsWith(".js") ? url : `${url}.js`;
    }

    async refreshCartSections() {
      const targets = Array.from(document.querySelectorAll("cart-items, mini-cart"));
      const refreshes = targets
        .filter((target, index) => targets.indexOf(target) === index)
        .map((target) => target.updateSectionContents?.())
        .filter(Boolean);

      if (!refreshes.length) {
        return;
      }

      await Promise.all(refreshes);
    }

    announce(message) {
      let liveRegion = document.querySelector("[data-cart-vehicle-live]");

      if (!liveRegion) {
        liveRegion = document.createElement("p");
        liveRegion.className = "visually-hidden";
        liveRegion.setAttribute("aria-live", "polite");
        liveRegion.setAttribute("role", "status");
        liveRegion.dataset.cartVehicleLive = "true";
        document.body.append(liveRegion);
      }

      liveRegion.textContent = "";
      window.setTimeout(() => {
        liveRegion.textContent = message;
      }, 0);
    }

    focusAfterRepair() {
      const target =
        document.querySelector("#checkout:not([disabled])") ||
        document.querySelector('mini-cart button[name="checkout"]:not([disabled])') ||
        document.querySelector("[data-cart-vehicle-preflight]");

      target?.focus?.();
    }
  }

  if (!window.cartVehiclePreflight) {
    window.cartVehiclePreflight = new CartVehiclePreflight();
  }
})();
